import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import {
    SCHEDULE_PRESET_KEYS, schedulePresetDate, combineDateTime, toDateInputValue, toTimeInputValue,
} from '../../lib/schedulePresets';

const PRESET_LABELS = {
    in2hours: <Text id='support.schedule.in2hours'>In 2 hours</Text>,
    tomorrow8: <Text id='support.schedule.tomorrow8'>Tomorrow 08:00</Text>,
    nextMonday8: <Text id='support.schedule.nextMonday8'>Next Monday 08:00</Text>,
};

@observer
class ScheduleReplyDrawer extends Component {
    constructor(props) {
        super(props);
        const initial = schedulePresetDate('tomorrow8');
        this.state = {
            selection: 'tomorrow8',
            customDate: toDateInputValue(initial),
            customTime: toTimeInputValue(initial),
            scheduleSendEmail: true,
            scheduleSendSms: false,
            smsBody: '',
            saving: false,
            error: null,
        };
    }

    closeDrawer = () => {
        const { appState } = this.props.stores;
        const { drawerLevel } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    selectPreset = (key) => {
        const d = schedulePresetDate(key);
        this.setState({ selection: key, customDate: toDateInputValue(d), customTime: toTimeInputValue(d) });
    }

    onDateChange = (e) => this.setState({ selection: 'custom', customDate: e.target.value });
    onTimeChange = (e) => this.setState({ selection: 'custom', customTime: e.target.value });

    resolveDate = () => combineDateTime(this.state.customDate, this.state.customTime);

    handleSchedule = async () => {
        const {
            stores, ticket, body, images = [], files = [], existingUuidv4, onSaved,
        } = this.props;
        const { supportTicketStore } = stores;
        const {
            scheduleSendEmail, scheduleSendSms, smsBody,
        } = this.state;
        const when = this.resolveDate();
        if (!when || when.getTime() <= Date.now()) {
            this.setState({ error: 'pickFuture' });
            return;
        }
        if (!scheduleSendEmail && !scheduleSendSms) {
            this.setState({ error: 'pickChannel' });
            return;
        }
        this.setState({ saving: true, error: null });
        try {
            const payload = {
                body, images, files,
                scheduledPublishDate: when.toISOString(),
                scheduleSendEmail, scheduleSendSms, smsBody,
            };
            // Converting an existing draft: PATCH the entry (status → scheduled) instead
            // of creating a second one.
            const response = existingUuidv4
                ? await supportTicketStore.editScheduledReply(ticket.uuidv4, existingUuidv4, { ...payload, status: 'scheduled' })
                : await supportTicketStore.scheduleReply(ticket.uuidv4, payload);
            if (response.status === 201 || response.status === 200) {
                if (typeof onSaved === 'function') await onSaved();
                this.closeDrawer();
            } else {
                this.setState({ error: response.message || 'failed' });
            }
        } finally {
            this.setState({ saving: false });
        }
    }

    render() {
        const {
            selection, customDate, customTime, scheduleSendEmail, scheduleSendSms, smsBody, saving, error,
        } = this.state;
        return (
            <div class="d-flex flex-column gap-3 p-4 mb-5">
                <h5 class="mb-1">
                    <i class="fas fa-clock me-2" style="color: #8b5cf6;" />
                    <Text id='support.schedule.title'>Schedule this answer</Text>
                </h5>
                <p class="text-body-secondary small mb-2">
                    <Text id='support.schedule.help'>The answer stays a hidden draft only you can see, then is sent at the time you pick — stamped with that time.</Text>
                </p>

                <div class="d-flex flex-wrap gap-2">
                    {SCHEDULE_PRESET_KEYS.map(key => (
                        <button key={key} type="button"
                            class={`btn btn-sm ${selection === key ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.selectPreset(key)}>
                            {PRESET_LABELS[key]}
                        </button>
                    ))}
                </div>

                <div class="d-flex gap-2">
                    <div class="flex-grow-1">
                        <label class="form-label small mb-1"><Text id='support.schedule.date'>Date</Text></label>
                        <input type="date" class="form-control" value={customDate} onInput={this.onDateChange} />
                    </div>
                    <div>
                        <label class="form-label small mb-1"><Text id='support.schedule.time'>Time</Text></label>
                        <input type="time" class="form-control" value={customTime} onInput={this.onTimeChange} />
                    </div>
                </div>

                <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="schEmail" checked={scheduleSendEmail}
                        onChange={e => this.setState({ scheduleSendEmail: e.target.checked })} />
                    <label class="form-check-label" for="schEmail"><Text id='support.schedule.email'>Send as email</Text></label>
                </div>
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="schSms" checked={scheduleSendSms}
                        onChange={e => this.setState({ scheduleSendSms: e.target.checked })} />
                    <label class="form-check-label" for="schSms"><Text id='support.schedule.sms'>Send as SMS</Text></label>
                </div>
                {scheduleSendSms && (
                    <div>
                        <label class="form-label small mb-1"><Text id='support.schedule.smsBody'>SMS text (optional — defaults to the answer)</Text></label>
                        <textarea class="form-control" rows="2" value={smsBody} onInput={e => this.setState({ smsBody: e.target.value })} />
                    </div>
                )}

                {error && (
                    <div class="alert alert-warning py-2 mb-0">
                        <Text id='support.schedule.errorGeneric'>Could not schedule. Check the date/time and that a channel is selected.</Text>
                    </div>
                )}

                <div class="d-flex justify-content-end gap-2 mt-2">
                    <button type="button" class="btn btn-outline-secondary" onClick={this.closeDrawer} disabled={saving}>
                        <Text id='support.schedule.cancel'>Cancel</Text>
                    </button>
                    <button type="button" class="btn btn-primary" onClick={this.handleSchedule} disabled={saving}>
                        {saving
                            ? <><i class="fas fa-spinner fa-spin me-1" /><Text id='support.schedule.saving'>Scheduling...</Text></>
                            : <Text id='support.schedule.set'>Schedule send</Text>}
                    </button>
                </div>
            </div>
        );
    }
}

export default ScheduleReplyDrawer;
